import { useEffect, useRef } from 'react';

import { RadioButton } from 'cpool-components/input/RadioButton';
import type { ApplicantCreation, Schema_Wohnsituation } from 'cpool-schemas';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useIsSameHousehold } from '../../util/hooks/useIsSameHousehold';
import { mapMainApplicantWohnsituation } from '../../util/mapMainApplicantWohnsituation';
import type { CreateApplicantFormValues } from '../../util/schemaCreateApplicantForm';

type Props = {
    mainApplicantWohnsituation: Schema_Wohnsituation;
    savedWohnsituation: CreateApplicantFormValues['wohnsituation'] | null;
    onSaveWohnsituationValues: (values: CreateApplicantFormValues['wohnsituation']) => void;
};

export const SameHouseholdSelector: React.FC<Props> = ({
    mainApplicantWohnsituation,
    onSaveWohnsituationValues,
    savedWohnsituation,
}) => {
    const { t } = useTranslation();
    const { getValues, setValue } = useFormContext<ApplicantCreation>();
    const isSameHousehold = useIsSameHousehold();
    const savedWohnsituationRef = useRef<CreateApplicantFormValues['wohnsituation'] | null>(savedWohnsituation);

    useEffect(() => {
        if (savedWohnsituation) {
            savedWohnsituationRef.current = savedWohnsituation;
        }
    }, [savedWohnsituation]);

    const handleChange = (sameHousehold: boolean) => {
        if (sameHousehold === isSameHousehold) {
            return;
        }

        if (sameHousehold) {
            const ownWohnsituation = getValues('wohnsituation');
            savedWohnsituationRef.current = ownWohnsituation;
            onSaveWohnsituationValues(ownWohnsituation);

            setValue('wohnsituation', mapMainApplicantWohnsituation(mainApplicantWohnsituation), {
                shouldDirty: true,
                shouldValidate: true,
            });
            setValue('wohnsituation.anschrift.wohnhaftSeit', ownWohnsituation.anschrift?.wohnhaftSeit ?? null, {
                shouldDirty: true,
                shouldValidate: true,
            });
            return;
        }

        const wohnsituationToRestore = savedWohnsituationRef.current ?? savedWohnsituation;
        if (wohnsituationToRestore) {
            setValue('wohnsituation', wohnsituationToRestore, {
                shouldDirty: true,
                shouldValidate: true,
            });
            return;
        }

        setValue('wohnsituation.gemeinsamerHaushalt', false, {
            shouldDirty: true,
            shouldValidate: true,
        });
    };

    return (
        <div className="flex items-center gap-[18px] pb-4">
            <span className="w-[380px] shrink-0 text-sm text-midnight text-left">
                {t('LIVING_SITUATION_DATA.SAME_HOUSEHOLD_AS_MAIN_APPLICANT')}
            </span>
            <div className="flex h-8 shrink-0 items-center gap-[7.5px]">
                <RadioButton
                    wrapperClassName="h-8 gap-2 rounded px-2.5"
                    id="create-applicant-same-household-yes"
                    name="create-applicant-same-household"
                    value="true"
                    label={t('VALUES.YES')}
                    checked={isSameHousehold}
                    onChange={() => handleChange(true)}
                />
                <RadioButton
                    wrapperClassName="h-8 min-w-[83px] gap-2 rounded px-2.5"
                    id="create-applicant-same-household-no"
                    name="create-applicant-same-household"
                    value="false"
                    label={t('VALUES.NO')}
                    checked={!isSameHousehold}
                    onChange={() => handleChange(false)}
                />
            </div>
        </div>
    );
};
